// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { useMemo, useState } from 'react'
import {
  Checkbox,
  Table,
  Typography,
  Flex,
  ColumnDef,
} from '@teamnine/pcj-ui-v2'
import { Storage } from '@/utils/material-icons'
import { useTheme } from 'styled-components'

import { Template as ITemplate } from '@/services/templates'
import StatusTag from '@/app/shared/components/StatusTag'

type ObjectDataTableProps = {
  template?: ITemplate
}

type ObjectDataRow = {
  model: string
  numberRecords: number
  status?: string
}

export default function ObjectDataTable({ template }: ObjectDataTableProps) {
  const { colors } = useTheme()

  const [showEmptyObjects, setShowEmptyObjects] = useState(false)

  const objectData = useMemo(() => template?.objectData ?? [], [template])

  const filteredData = useMemo(() => {
    if (showEmptyObjects) return objectData
    return objectData.filter((obj) => obj.numberRecords > 0)
  }, [objectData, showEmptyObjects])

  const columns = useMemo<ColumnDef<ObjectDataRow>[]>(
    () => [
      {
        id: 'model',
        header: 'Objects',
        accessorKey: 'model' as const,
        enableSorting: false,
        cell: ({ row }: { row: { original: ObjectDataRow } }) => (
          <Typography color={colors.ngprimary950}>
            {row.original.model}
          </Typography>
        ),
      },
      {
        id: 'numberRecords',
        header: 'Records',
        accessorKey: 'numberRecords' as const,
        enableSorting: false,
        cell: ({ row }: { row: { original: ObjectDataRow } }) => (
          <Typography>{row.original.numberRecords}</Typography>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status' as const,
        enableSorting: false,
        cell: ({ row }: { row: { original: ObjectDataRow } }) => (
          <StatusTag status={row?.original.status} />
        ),
      },
    ],
    [colors.ngprimary950]
  )

  return (
    <Flex direction="column" gap={16}>
      <Flex align="center" justify="space-between">
        <Typography variant="14-medium">Object Data Overview</Typography>
        <Checkbox
          checked={showEmptyObjects}
          onChange={() => setShowEmptyObjects((prev) => !prev)}
        >
          Show Objects with no records
        </Checkbox>
      </Flex>
      <Table
        data={filteredData}
        columns={columns}
        isLoading={!template}
        empty={{
          title: 'There is no object data.',
          Icon: Storage,
        }}
      />
    </Flex>
  )
}
